import { Timestamp } from 'firebase/firestore';
import { z } from 'zod';

/** ISO-8601 date-time string. We store timestamps as ISO strings in Firestore for portability. */
export const isoDateTime = z.string().datetime({ offset: true });

/** Slug: lowercase, alphanumeric, hyphen-separated. */
export const slugSchema = z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Invalid slug');

export const currencySchema = z.literal('MAD');

/**
 * Firestore `serverTimestamp()` writes a Timestamp object — on read, that
 * field is a Timestamp instance (not the ISO string our schemas validate
 * against). This helper coerces whatever shape we get back into a canonical
 * ISO-8601 string so Zod parsing stays clean.
 *
 * Handles:
 * - Firestore Timestamp instances (Web SDK)
 * - Already-ISO strings (our seed writes these directly)
 * - `{ seconds, nanoseconds }` plain objects (some SDK paths surface them this way)
 * - Anything else → fallback (or "now")
 */
export function toIsoString(value: unknown, fallback?: string): string {
  if (value instanceof Timestamp) return value.toDate().toISOString();
  if (typeof value === 'string' && value.length > 0) return value;
  if (
    value &&
    typeof value === 'object' &&
    'seconds' in value &&
    typeof (value as { seconds: unknown }).seconds === 'number'
  ) {
    const seconds = (value as { seconds: number }).seconds;
    const nanos = (value as { nanoseconds?: number }).nanoseconds ?? 0;
    return new Date(seconds * 1000 + nanos / 1_000_000).toISOString();
  }
  return fallback ?? new Date().toISOString();
}
